import { DecoratorNode, $getRoot } from 'lexical';
import { $convertFromMarkdownString, $convertToMarkdownString } from '@lexical/markdown';
import { PLAYGROUND_TRANSFORMERS } from '../MarkdownTransformers.ts';
import { ServerURL } from '../../ServerURL';
// Using DOM traversal instead of Lexical node methods to avoid timing issues

/**
 * Make API call to get more details for a section
 */
async function getMoreDetails(meetingId, sectionContent) {
  try {
    const formData = new FormData();
    formData.append('part_to_detail', sectionContent);
    
    const response = await fetch(`${ServerURL}/get_more_details/${meetingId}`, {
      method: 'POST',
      mode: 'cors',
      body: formData
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('API endpoint not found - feature not implemented yet');
      } else if (response.status === 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    const result = await response.json();
    const enhancedContent = result.enhanced_content || result.content || result || sectionContent;
    
    console.log('Raw API response content:', enhancedContent);
    return enhancedContent;
  } catch (error) {
    console.error('Error calling get_more_details API:', error);
    throw error;
  }
}

/**
 * Replace section content with enhanced content from backend using clean header text
 * Uses a simple and reliable approach: find section in markdown, replace it, reload editor
 */
function replaceSectionContentWithText(cleanHeaderText, headerElement, enhancedSectionMarkdown, editor) {
  try {
    console.log('Starting section replacement...');
    console.log('Enhanced content from backend:', enhancedSectionMarkdown);
    console.log('Using clean header text:', cleanHeaderText);
    
    const headerLevel = parseInt(headerElement.tagName.replace('H', ''));
    const headerMarkdown = '#'.repeat(headerLevel) + ' ' + cleanHeaderText;
    
    console.log('Looking for header in document:', headerMarkdown);
    
    editor.update(() => {
      // Get current document as markdown
      const currentMarkdown = $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);
      console.log('Current document markdown:', currentMarkdown);
      
      // Split into lines
      const lines = currentMarkdown.split('\n');
      
      // Find the header line index using clean text
      let headerIndex = -1;
      
      // Strategy 1: Exact match
      headerIndex = lines.findIndex(line => line.trim() === headerMarkdown.trim());
      
      // Strategy 2: Direct text match in line
      if (headerIndex === -1) {
        headerIndex = lines.findIndex(line => line.includes(cleanHeaderText));
      }
      
      // Strategy 3: Contains header text and has # symbol
      if (headerIndex === -1) {
        headerIndex = lines.findIndex(line => 
          line.includes('#') && line.includes(cleanHeaderText)
        );
      }
      
      console.log('Header matching strategies tried. Found at index:', headerIndex);
      
      if (headerIndex === -1) {
        console.error('Could not find header in markdown. Available lines:', lines);
        alert(`Could not find header "${cleanHeaderText}" in document for replacement`);
        return;
      }
      
      console.log('Found header at line', headerIndex, ':', lines[headerIndex]);
      
      // Find where this section ends (next header of same or higher level)
      let sectionEndIndex = lines.length;
      for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('#')) {
          const lineLevel = line.match(/^#+/)?.[0]?.length || 0;
          if (lineLevel <= headerLevel) {
            sectionEndIndex = i;
            console.log('Section ends at line', i, ':', line);
            break;
          }
        }
      }
      
      // Build new document: before + enhanced + after
      const beforeLines = lines.slice(0, headerIndex);
      const afterLines = lines.slice(sectionEndIndex);
      const enhancedLines = enhancedSectionMarkdown.split('\n');
      
      const newMarkdown = [
        ...beforeLines,
        ...enhancedLines,
        ...afterLines
      ].join('\n');
      
      console.log('📄 Original document length:', currentMarkdown.length, 'characters');
      console.log('📄 New document length:', newMarkdown.length, 'characters');
      console.log('🔄 Content comparison:', currentMarkdown === newMarkdown ? 'identical' : 'different');
      
      console.log('Replacing editor content with server response...');
      
      // Save current scroll position before replacing content
      const editorElement = editor.getRootElement();
      const scrollContainer = editorElement?.closest('.editor-inner') || 
                            editorElement?.closest('.editor-container') ||
                            editorElement?.parentElement ||
                            window;
      
      let savedScrollTop = 0;
      let savedScrollLeft = 0;
      
      if (scrollContainer === window) {
        savedScrollTop = window.scrollY || window.pageYOffset;
        savedScrollLeft = window.scrollX || window.pageXOffset;
      } else if (scrollContainer) {
        savedScrollTop = scrollContainer.scrollTop;
        savedScrollLeft = scrollContainer.scrollLeft;
      }
      
      console.log('💾 Saved scroll position:', { top: savedScrollTop, left: savedScrollLeft });
      
      // Clear editor and load new content
      const root = $getRoot();
      root.clear();
      $convertFromMarkdownString(newMarkdown, PLAYGROUND_TRANSFORMERS);
      
      console.log('✅ Section replacement completed successfully - content updated!');
      
      // Restore scroll position after a brief delay to allow content to render
      setTimeout(() => {
        try {
          if (scrollContainer === window) {
            window.scrollTo(savedScrollLeft, savedScrollTop);
          } else if (scrollContainer) {
            scrollContainer.scrollTop = savedScrollTop;
            scrollContainer.scrollLeft = savedScrollLeft;
          }
          console.log('📍 Scroll position restored');
        } catch (scrollError) {
          console.warn('Could not restore scroll position:', scrollError);
        }
      }, 10); // Small delay to ensure content is rendered
    });
    
  } catch (error) {
    console.error('❌ Error in section replacement:', error);
    alert(`Error replacing section: ${error.message}`);
  }
}

// Removed old replaceSectionContent function - using replaceSectionContentWithText instead

/**
 * Get the actual markdown content of a section using provided header text
 */
function getSectionMarkdownWithText(headerText, headerElement, editor) {
  return editor.getEditorState().read(() => {
    try {
      // Get the entire document as markdown
      const fullMarkdown = $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);
      console.log('Full document markdown:', fullMarkdown);
      
      // Split into lines and find our section using the original header text
      const lines = fullMarkdown.split('\n');
      const headerLevel = parseInt(headerElement.tagName.replace('H', ''));
      const expectedHeaderMarkdown = '#'.repeat(headerLevel) + ' ' + headerText;
      
      console.log('Looking for header:', expectedHeaderMarkdown);
      console.log('Available lines:', lines.slice(0, 10));
      
      // Find the header line index - try multiple matching strategies
      let headerLineIndex = -1;
      
      // Strategy 1: Exact match
      headerLineIndex = lines.findIndex(line => line.trim() === expectedHeaderMarkdown.trim());
      
      // Strategy 2: Direct text match in line
      if (headerLineIndex === -1) {
        headerLineIndex = lines.findIndex(line => line.includes(headerText));
      }
      
      // Strategy 3: Contains header text and has # symbol
      if (headerLineIndex === -1) {
        headerLineIndex = lines.findIndex(line => 
          line.includes('#') && line.includes(headerText)
        );
      }
      
      console.log('Found header at line index:', headerLineIndex);
      
      if (headerLineIndex === -1) {
        throw new Error(`Could not find header "${headerText}" in markdown`);
      }
      
      // Collect lines from header until next same/higher level header
      const sectionLines = [lines[headerLineIndex]]; // Include header
      
      for (let i = headerLineIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Check if this is a header line
        if (line.startsWith('#')) {
          const lineLevel = line.match(/^#+/)?.[0]?.length || 0;
          if (lineLevel <= headerLevel) {
            console.log('Stopping at next header:', line);
            break; // Stop at same or higher level header
          }
        }
        
        sectionLines.push(lines[i]);
      }
      
      const result = sectionLines.join('\n').trim();
      console.log('Extracted section markdown:', result);
      return result;
      
    } catch (error) {
      console.error('Error extracting section markdown:', error);
      throw error;
    }
  });
}

// Removed old getSectionMarkdown function - using getSectionMarkdownWithText instead

/**
 * Get DOM section content using provided header text
 */
function getDOMSectionContentWithText(headerText, headerElement) {
  const headerLevel = parseInt(headerElement.tagName.replace('H', ''));
  const editorRoot = headerElement.closest('.editor-input') || headerElement.closest('[contenteditable]');
  
  if (!editorRoot) {
    return 'Could not find editor root';
  }
  
  let sectionContent = [];
  
  // Add the header text itself using the original text
  sectionContent.push(`${headerElement.tagName}: ${headerText}`);
  sectionContent.push(''); // Empty line after header
  
  // Get all elements after this header
  let currentElement = headerElement.nextElementSibling;
  
  while (currentElement) {
    // If we hit another heading of same or higher level, stop
    if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(currentElement.tagName)) {
      const currentLevel = parseInt(currentElement.tagName.replace('H', ''));
      if (currentLevel <= headerLevel) {
        break; // Stop at same or higher level header
      }
    }
    
    // Handle different element types with proper formatting
    if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(currentElement.tagName)) {
      const headerText = currentElement.textContent.trim();
      if (headerText) {
        sectionContent.push(`${currentElement.tagName}: ${headerText}`);
      }
    } else if (currentElement.tagName === 'UL') {
      // Handle unordered lists
      const listItems = currentElement.querySelectorAll('li');
      if (listItems.length > 0) {
        sectionContent.push(''); // Empty line before list
        listItems.forEach(item => {
          const itemText = item.textContent.trim();
          if (itemText) {
            sectionContent.push(`• ${itemText}`);
          }
        });
      }
    } else if (currentElement.tagName === 'OL') {
      // Handle ordered lists
      const listItems = currentElement.querySelectorAll('li');
      if (listItems.length > 0) {
        sectionContent.push(''); // Empty line before list
        listItems.forEach((item, index) => {
          const itemText = item.textContent.trim();
          if (itemText) {
            sectionContent.push(`${index + 1}. ${itemText}`);
          }
        });
      }
    } else if (currentElement.tagName === 'BLOCKQUOTE') {
      // Handle blockquotes
      const quoteText = currentElement.textContent.trim();
      if (quoteText) {
        sectionContent.push(''); // Empty line before quote
        sectionContent.push(`> ${quoteText}`);
      }
    } else {
      // Handle paragraphs and other elements
      const elementText = currentElement.textContent.trim();
      if (elementText) {
        sectionContent.push(elementText);
      }
    }
    
    currentElement = currentElement.nextElementSibling;
  }
  
  return sectionContent.length > 2 
    ? sectionContent.join('\n')
    : sectionContent.join('\n') + '\n(No content in this section)';
}

// Removed old getDOMSectionContent function - using getDOMSectionContentWithText instead

/**
 * DetailRequestButtonNode - A DecoratorNode that renders a button
 * Following the official Lexical guide pattern for custom nodes
 */
export class DetailRequestButtonNode extends DecoratorNode {
  __meetingId;
  __editor;

  static getType() {
    return 'detail-request-button';
  }

  static clone(node) {
    return new DetailRequestButtonNode(node.__meetingId, node.__editor, node.__key);
  }

  constructor(meetingId, editor, key) {
    super(key);
    this.__meetingId = meetingId;
    this.__editor = editor;
  }


  /**
   * Create DOM element for the button
   * Following the guide's createDOM pattern
   */
  createDOM(config) {
    // Create a wrapper span that's completely isolated from content
    const wrapper = document.createElement('span');
    wrapper.className = 'detail-request-button-wrapper';
    wrapper.contentEditable = false; // Make wrapper non-editable
    wrapper.style.cssText = `
      display: inline-block;
      margin-left: 8px;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      pointer-events: auto;
    `;
    
    const button = document.createElement('button');
    button.className = 'detail-request-button-node';
    button.innerHTML = '+';
    button.contentEditable = false; // Make button non-editable
    button.style.cssText = `
      background-color: var(--detailRequestButtonColor, #6f046f);
      color: white;
      border: none;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      font-size: 12px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: middle;
      flex-shrink: 0;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      pointer-events: auto;
    `;
    
    // Add hover effects
    button.addEventListener('mouseenter', () => {
      button.style.backgroundColor = 'var(--detailRequestButtonHoverColor, #540061)';
    });
    
    button.addEventListener('mouseleave', () => {
      button.style.backgroundColor = 'var(--detailRequestButtonColor, #6f046f)';
    });
    
    // No need for node reference since we use DOM traversal
    
    // Store references for the click handler
    const meetingId = this.__meetingId;
    const editor = this.__editor;
    
    // Add click handler that gets enhanced content from backend
    button.addEventListener('click', async function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Store original button state outside try block for finally block
      const originalText = button.innerHTML;
      
      try {
        // Find the heading element in the DOM by traversing up BEFORE changing button state
        let currentElement = button.parentElement;
        while (currentElement && !['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(currentElement.tagName)) {
          currentElement = currentElement.parentElement;
        }
        
        if (!currentElement) {
          throw new Error('Could not find header element');
        }
        
        // Get the header text BEFORE changing button to loading state
        // Extract only the actual text content, excluding button elements
        let originalHeaderText = '';
        
        // Get text from all text nodes, skip button elements
        const walker = document.createTreeWalker(
          currentElement,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: function(node) {
              // Skip text nodes that are inside button elements
              const parent = node.parentElement;
              if (parent && (parent.classList.contains('detail-request-button-node') || 
                           parent.classList.contains('detail-request-button-wrapper'))) {
                return NodeFilter.FILTER_REJECT;
              }
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );
        
        let textNode = walker.nextNode();
        while (textNode) {
          originalHeaderText += textNode.textContent;
          textNode = walker.nextNode();
        }
        
        originalHeaderText = originalHeaderText.trim();
        console.log('Original header text (pure text only):', originalHeaderText);
        
        // NOW show loading state - using same style as audio controls
        button.innerHTML = '';
        button.disabled = true;
        
        // Create spinning loader element
        const loader = document.createElement('div');
        loader.style.cssText = `
          width: 12px;
          height: 12px;
          border: 2px solid;
          border-color: white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        `;
        
        // Add keyframes animation if not already present
        if (!document.querySelector('#detail-button-spinner-styles')) {
          const style = document.createElement('style');
          style.id = 'detail-button-spinner-styles';
          style.textContent = `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `;
          document.head.appendChild(style);
        }
        
        button.appendChild(loader);
        
        if (!meetingId) {
          throw new Error('Meeting ID not available');
        }
        
        // Extract section content using the original header text
        let sectionContent;
        try {
          const fullMarkdown = editor.getEditorState().read(() => {
            return $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);
          });
          
          console.log('Full markdown document:', fullMarkdown);
          
          // Try to extract section markdown using original header text
          sectionContent = getSectionMarkdownWithText(originalHeaderText, currentElement, editor);
        } catch (markdownError) {
          console.warn('Could not extract markdown, using DOM content:', markdownError.message);
          sectionContent = getDOMSectionContentWithText(originalHeaderText, currentElement);
        }
        
        // Dispatch custom event to open modal (Step 1 - Confirmation)
        const modalEvent = new CustomEvent('openDetailRequestModal', {
          detail: {
            title: originalHeaderText,
            originalContent: sectionContent,
            meetingId,
            onApply: (enhancedContent) => {
              // This callback will be called when user clicks Apply in modal
              if (editor && enhancedContent) {
                console.log('🔄 Replacing section with server response...');
                replaceSectionContentWithText(originalHeaderText, currentElement, enhancedContent, editor);
                console.log('✅ Section replacement completed');
              }
            }
          }
        });
        
        window.dispatchEvent(modalEvent);
        
        // Set up listener for proceed confirmation from modal
        const proceedHandler = async (event) => {
          if (event.detail.confirmed) {
            // User confirmed in step 1, now make API call
            try {
              console.log('🌐 User confirmed - Calling API with content:', sectionContent);
              const enhancedContent = await getMoreDetails(meetingId, sectionContent);
              console.log('📥 API returned enhanced content:', enhancedContent);
              console.log('📥 Content type:', typeof enhancedContent);
              console.log('📥 Content length:', enhancedContent?.length);
              
              // Dispatch event to update modal with enhanced content (Step 2)
              const contentEvent = new CustomEvent('updateDetailRequestModal', {
                detail: {
                  enhancedContent,
                  onApply: (content) => {
                    // This callback will be called when user clicks Apply in modal
                    if (editor && content) {
                      console.log('🔄 Replacing section with server response...');
                      replaceSectionContentWithText(originalHeaderText, currentElement, content, editor);
                      console.log('✅ Section replacement completed');
                    }
                  }
                }
              });
              
              window.dispatchEvent(contentEvent);
              
            } catch (apiError) {
              console.warn('❌ API call failed:', apiError.message);
              
              // Dispatch event to show error in modal
              const errorEvent = new CustomEvent('errorDetailRequestModal', {
                detail: {
                  error: apiError
                }
              });
              
              window.dispatchEvent(errorEvent);
            }
          }
          
          // Clean up the listener
          window.removeEventListener('proceedDetailRequestModal', proceedHandler);
        };
        
        // Listen for proceed confirmation
        window.addEventListener('proceedDetailRequestModal', proceedHandler);
        
      } catch (error) {
        console.error('Error getting section content:', error);
        
        // Dispatch event to show error in modal
        const errorEvent = new CustomEvent('errorDetailRequestModal', {
          detail: {
            error: error
          }
        });
        
        window.dispatchEvent(errorEvent);
      } finally {
        // Restore button state - clear any loader and restore original text
        button.innerHTML = originalText;
        button.disabled = false;
      }
    });
    
    // Add button to wrapper and return wrapper
    wrapper.appendChild(button);
    return wrapper;
  }

  updateDOM() {
    return false; // No updates needed - we retrieve text dynamically
  }

  /**
   * Serialization methods following the guide's pattern
   */
  static importJSON(serializedNode) {
    // Don't import detail request buttons - they should be recreated by transforms
    return null;
  }

  exportJSON() {
    // Don't export detail request buttons - they are UI-only, not content
    return null;
  }

  // Exclude from all serialization
  exportDOM() {
    return null;
  }

  static importDOM() {
    return null;
  }

  getTextContent() {
    return ''; // Button contributes no text content to the editor
  }
  
  // Override to ensure button doesn't affect text selection
  getTextContentSize() {
    return 0;
  }

  isInline() {
    return true;
  }

  /**
   * Make the button non-selectable to prevent deletion
   */
  isKeyboardSelectable() {
    return false;
  }

  /**
   * Make the button non-deletable
   */
  canBeEmpty() {
    return false;
  }


  /**
   * DecoratorNode requires this method - we return null since we handle rendering in createDOM
   */
  decorate() {
    return null;
  }
}

/**
 * Factory function following the guide's pattern
 */
export function $createDetailRequestButtonNode(meetingId, editor) {
  return new DetailRequestButtonNode(meetingId, editor);
}

/**
 * Type check function following the guide's pattern
 */
export function $isDetailRequestButtonNode(node) {
  return node instanceof DetailRequestButtonNode;
}